import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { mediaUrl, extractErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/lib/cart";
import { useCartUI } from "@/lib/cartui";
import { useWishlist } from "@/lib/wishlist";
import { useCustomerAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Minus, Plus, ShoppingCart, Check, ChevronLeft, ChevronRight, Heart, Star, Truck, ShieldCheck, IndianRupee, Headset, Sparkles, Award, Clock, Package, Leaf, Zap, Gift, ThumbsUp, BadgeCheck, ShoppingBag, Phone, MapPin, Thermometer, Wind, Droplets, Shirt, Snowflake, Flame, Recycle, Waves, Wand2, Sun, Smile, Flower2, Baby } from "lucide-react";
import { Stars } from "./ProductCard";
import RelatedProductSlider from "./RelatedProductSlider";
import SeoMeta from "@/components/SeoMeta";
import { useStoreBase } from "@/lib/store";
import { useTranslation } from "react-i18next";
import { isWhyUsEmoji } from "@/lib/whyUsIcons";
import { gallerySlides, youtubeEmbed, youtubeThumb } from "@/lib/productGallery";
import ProductModelViewer from "@/components/ProductModelViewer";
import CustomProductForm from "./CustomProductForm";
import ProductCustomizer from "./ProductCustomizer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { isPrintOn, renderPrintPreviewBlob } from "@/lib/customPrint";
import { money } from "@/lib/money";
import { storefrontCardVariant } from "@/lib/storefrontTheme";
import { productCategoryIds } from "@/lib/productCategories";
import { looksLikeHtml, sanitizeProductHtml, stripHtml } from "@/lib/productDescription";
import { storeOrdersPaused, storeOfflineMessage } from "@/lib/timeslotCheckout";
import { trackStoreEvent } from "@/lib/storeAds";
import { CosmosBestsellerTags, CosmosCreativeInfo, CosmosDescriptionTabs, CosmosProductFaq } from "./CosmosProductPanels";

const ICONS = {
  Truck, ShieldCheck, IndianRupee, Headset, Star, Sparkles, Award, Heart,
  Clock, Package, Leaf, Zap, Gift, ThumbsUp, BadgeCheck, ShoppingBag, Phone, MapPin,
  Thermometer, Wind, Droplets, Shirt, Snowflake, Flame, Recycle, Waves,
  Sun, Smile, Flower2, Baby,
};
const OFFER_RIBBON = {
  sky: "bg-[#2f6bff] text-white",
  amber: "bg-amber-400 text-stone-900",
  emerald: "bg-emerald-500 text-white",
  violet: "bg-violet-500 text-white",
};
const OFFER_BUTTON = {
  sky: "bg-[#2f6bff] text-white",
  amber: "bg-amber-400 text-stone-900",
  emerald: "bg-emerald-600 text-white",
  violet: "bg-violet-600 text-white",
};
function reviewCountLabel(n) {
  const v = Number(n) || 0;
  if (v >= 1000) {
    const k = v / 1000;
    return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")}K+`;
  }
  return String(v);
}
function IconByName({ name, className }) {
  if (isWhyUsEmoji(name)) return <span className="text-xl leading-none" aria-hidden>{name}</span>;
  const C = ICONS[name] || Star;
  return <C className={className} />;
}

export default function StorefrontProduct({ config }) {
  const { t } = useTranslation();
  const { pSlug } = useParams();
  const [searchParams] = useSearchParams();
  const requireVariantPick = searchParams.get("select") === "variant";
  const { slug, base, country } = useStoreBase();
  const nav = useNavigate();
  const { add } = useCart();
  const { openCart, skipCart } = useCartUI();
  const wishlist = useWishlist();
  const { user } = useCustomerAuth();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [variantId, setVariantId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [ratingAgg, setRatingAgg] = useState({ rating_avg: 0, rating_count: 0 });
  const [myRating, setMyRating] = useState(0);
  const [hoverStar, setHoverStar] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("description");
  const [show3d, setShow3d] = useState(false);
  const [category, setCategory] = useState(undefined);
  const [printSnap, setPrintSnap] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [offers, setOffers] = useState([]);
  const offerRow = useRef(null);
  const enhancer = Boolean(config?.product_page_enhancer_active);
  const cosmosDesignOn = Boolean(config?.cosmos_design_enhancer_active);
  const cosmosDesign = p?.cosmos_design || null;

  useEffect(() => {
    if (!p) return;
    const ids = productCategoryIds(p);
    if (!ids.length) { setCategory(null); return; }
    setCategory(undefined);
    api.get(`/store/${slug}/categories`).then(({ data }) => {
      const c = (data || []).find((x) => ids.includes(x.id));
      setCategory(c || null);
    }).catch(() => setCategory(null));
  }, [slug, p]);

  const loadReviews = () => api.get(`/store/${slug}/products/${pSlug}/reviews`).then(({ data }) => {
    setReviews(data.reviews || []);
    setRatingAgg({ rating_avg: data.rating_avg || 0, rating_count: data.rating_count || 0 });
    if (data.my_rating) setMyRating(data.my_rating);
  }).catch(() => {});

  useEffect(() => {
    if (!p || p === false) return;
    trackStoreEvent("view_item", { value: Number(p.price || 0), currency: "INR", content_ids: [p.id], content_name: p.name });
  }, [p]);

  useEffect(() => {
    api.get(`/store/${slug}/products/${pSlug}`, { params: country ? { country } : {} }).then(({ data }) => {
      setP(data);
      setRatingAgg({ rating_avg: data.rating_avg || 0, rating_count: data.rating_count || 0 });
      if (data.variants && data.variants.length > 0 && !requireVariantPick) setVariantId(data.variants[0].id);
      else setVariantId("");
    }).catch(() => setP(false));
    loadReviews();
  }, [slug, pSlug, country, requireVariantPick]);

  useEffect(() => {
    if (!enhancer) { setOffers([]); return; }
    api.get(`/store/${slug}/offers`).then(({ data }) => setOffers(data.items || [])).catch(() => setOffers([]));
  }, [slug, enhancer]);

  useEffect(() => {
    if (p?.model_3d_url) setShow3d(true);
  }, [p?.model_3d_url]);

  const submitRating = async (stars) => {
    if (!user) { toast.error("Please sign in to rate this product"); nav(`${base}/account/login`); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/store/${slug}/products/${pSlug}/rate`, { stars, comment: myComment });
      setMyRating(stars);
      setRatingAgg({ rating_avg: data.rating_avg, rating_count: data.rating_count });
      toast.success("Thanks for rating!");
      loadReviews();
    } catch (e) { toast.error(extractErr(e)); } finally { setSubmitting(false); }
  };

  const activeVariant = useMemo(() => {
    if (!p || !variantId) return null;
    return (p.variants || []).find((v) => v.id === variantId) || null;
  }, [p, variantId]);

  const effective = useMemo(() => {
    if (!p) return null;
    const price = activeVariant?.price || p.price;
    const mrp = activeVariant?.mrp || p.mrp;
    const stock = activeVariant ? activeVariant.stock : p.stock;
    const sku = activeVariant?.sku || p.sku;
    return { price, mrp, stock, sku };
  }, [p, activeVariant]);

  // Compose the image gallery: variant image (if set) first, then product images
  const gallery = useMemo(() => {
    if (!p) return [];
    const slides = gallerySlides(p.images);
    if (activeVariant?.image_url && !slides.some((s) => s.type === "image" && s.url === activeVariant.image_url)) {
      slides.unshift({ type: "image", url: activeVariant.image_url });
    }
    return slides;
  }, [p, activeVariant]);

  // When switching variants, jump to that variant's image if present
  useEffect(() => {
    if (activeVariant?.image_url) {
      const idx = gallery.findIndex((s) => s.type === "image" && s.url === activeVariant.image_url);
      if (idx >= 0) setActiveImg(idx);
    } else if (activeVariant) {
      setActiveImg(0);
    }
  }, [activeVariant?.id, gallery, activeVariant]);

  if (p === false) return <div className="p-16 text-center text-stone-500">Product not found</div>;
  if (!p || !effective) return <div className="p-16 text-center text-stone-500">Loading…</div>;

  const formOn = Boolean(p.custom_form?.enabled && (p.custom_form.fields || []).length);
  const printOn = isPrintOn(p);
  const fitment = p.car_parts?.enabled ? p.car_parts : null;
  const doAdd = async (formExtra, goCheckout = false) => {
    if (storeOrdersPaused(config)) { toast.error(storeOfflineMessage(config)); return; }
    if (p.variants && p.variants.length > 0 && !activeVariant) { toast.error("Please select a variation to add to cart"); return; }
    let extra = formExtra ? { custom_form: formExtra.values, qty: formExtra.qty, unit_price: formExtra.unit_price, snapshot: formExtra.snapshot } : null;
    if (printOn) {
      if (!printSnap?.image && !printSnap?.text) { toast.error("Customize the product first"); return; }
      let preview_url = "";
      try {
        const blob = await renderPrintPreviewBlob({
          baseUrl: p.custom_print.base_image_url,
          printArea: p.custom_print.print_area,
          image: printSnap.image,
          text: printSnap.text,
        });
        if (blob) {
          const fd = new FormData();
          fd.append("file", blob, "preview.jpg");
          const { data } = await api.post(`/store/${slug}/custom-print/upload`, fd);
          preview_url = data.url;
        }
      } catch {
        /* preview is optional; transforms still persist */
      }
      extra = { ...(extra || {}), custom_print: { ...printSnap, preview_url } };
    }
    if (extra) add(p, extra.qty || qty, activeVariant, extra);
    else add(p, qty, activeVariant);
    trackStoreEvent("add_to_cart", { value: Number(effective.price || 0) * (extra?.qty || qty || 1), currency: "INR", content_ids: [p.id], content_name: p.name });
    if (goCheckout || skipCart) { nav(`${base}/checkout`); return; }
    openCart();
    toast.success("Added to cart");
  };
  const buyNow = () => doAdd(undefined, true);

  const discount = effective.mrp && effective.mrp > effective.price ? Math.round(((effective.mrp - effective.price) / effective.mrp) * 100) : 0;
  const primary = config.branding?.primary_color || "#15803D";
  const trustLines = (config.product_settings?.trust_lines || []).filter((x) => x && x.text);
  const featureIcons = (config.product_settings?.feature_icons || []).filter((x) => x && x.text);
  const activeSlide = gallery[activeImg];
  const sym = p.currency_symbol || "₹";
  const stockHidden = formOn;
  const oos = !stockHidden && effective.stock != null && effective.stock <= 0;

  // Group variants by size/color axes so we render nice pickers
  const sizes = Array.from(new Set((p.variants || []).map((v) => v.attributes?.size).filter(Boolean)));
  const colors = Array.from(new Set((p.variants || []).map((v) => v.attributes?.color).filter(Boolean)));
  const colorHex = {};
  (p.variants || []).forEach((v) => { if (v.attributes?.color && v.attributes?.color_hex) colorHex[v.attributes.color] = v.attributes.color_hex; });

  const findVariant = (nextSize, nextColor) => {
    return (p.variants || []).find((v) => (nextSize ? v.attributes?.size === nextSize : true) && (nextColor ? v.attributes?.color === nextColor : true));
  };

  return (
    <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
      <SeoMeta endpoint={`/seo/${slug}/product/${pSlug}/meta`} fallback={{ title: p.name, description: stripHtml(p.description) }} />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="mb-6" data-testid="product-breadcrumb">
        <ol className="flex flex-wrap items-center gap-1 text-sm text-stone-500">
          <li><Link to={`${base}/`} className="hover:text-stone-900 transition-colors">Home</Link></li>
          <li className="text-stone-300"><ChevronRight className="w-4 h-4" /></li>
          <li><Link to={`${base}/shop`} className="hover:text-stone-900 transition-colors">Shop</Link></li>
          {category && (
            <>
              <li className="text-stone-300"><ChevronRight className="w-4 h-4" /></li>
              <li><Link to={`${base}/category/${category.slug}`} className="hover:text-stone-900 transition-colors">{category.name}</Link></li>
            </>
          )}
          <li className="text-stone-300"><ChevronRight className="w-4 h-4" /></li>
          <li className="text-stone-900 font-medium truncate max-w-[200px]" aria-current="page">{p.name}</li>
        </ol>
      </nav>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12 min-w-0">
      <div className="min-w-0 w-full max-w-full">
        <div className="relative aspect-square w-full max-w-full bg-stone-100 rounded-2xl overflow-hidden border border-stone-200/70 group"
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") setActiveImg((i) => Math.max(0, i - 1));
            else if (e.key === "ArrowRight") setActiveImg((i) => Math.min(gallery.length - 1, i + 1));
          }}
          tabIndex={0}
          onTouchStart={(e) => { window.__touchX = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            const dx = e.changedTouches[0].clientX - (window.__touchX || 0);
            if (Math.abs(dx) > 40) {
              if (dx < 0) setActiveImg((i) => Math.min(gallery.length - 1, i + 1));
              else setActiveImg((i) => Math.max(0, i - 1));
            }
          }}
        >
          {show3d && p.model_3d_url ? (
            <ProductModelViewer src={p.model_3d_url} alt={p.name} />
          ) : activeSlide ? (
            activeSlide.type === "youtube" ? (
              <iframe
                key={activeSlide.video_id}
                src={youtubeEmbed(activeSlide.video_id)}
                title={p.name}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="product-slider-youtube"
              />
            ) : (
              <img src={mediaUrl(activeSlide.url)} alt={p.name} className="w-full h-full max-w-full object-cover fade-in" key={activeSlide.url} data-testid="product-slider-image" />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-6xl">{p.name.slice(0, 1)}</div>
          )}

          {gallery.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous image"
                data-testid="product-slider-prev"
                onClick={() => setActiveImg((i) => (i - 1 + gallery.length) % gallery.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/95 shadow-md text-stone-900 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              ><ChevronLeft className="w-5 h-5" /></button>
              <button
                type="button"
                aria-label="Next image"
                data-testid="product-slider-next"
                onClick={() => setActiveImg((i) => (i + 1) % gallery.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/95 shadow-md text-stone-900 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              ><ChevronRight className="w-5 h-5" /></button>

              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Image ${i + 1}`}
                    data-testid={`product-slider-dot-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`h-1.5 rounded-full transition-all ${activeImg === i ? "bg-stone-900 w-6" : "bg-stone-900/40 w-2.5 hover:bg-stone-900/60"}`}
                  />
                ))}
              </div>

              <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-white/85 backdrop-blur-sm text-[11px] font-mono-nums text-stone-700">
                {activeImg + 1} / {gallery.length}
              </div>
            </>
          )}
        </div>
        {(gallery.length > 0 || p.model_3d_url) && (
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar max-w-full">
            {gallery.map((slide, i) => (
              <button key={i} onClick={() => { setShow3d(false); setActiveImg(i); }} className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${!show3d && activeImg === i ? "border-stone-900 scale-105" : "border-transparent hover:border-stone-300"}`} data-testid={`product-img-${i}`}>
                <img src={slide.type === "youtube" ? youtubeThumb(slide.video_id) : mediaUrl(slide.url)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
            {p.model_3d_url && (
              <button type="button" onClick={() => setShow3d(true)} className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 text-xs font-semibold bg-stone-100 ${show3d ? "border-stone-900 scale-105" : "border-transparent hover:border-stone-300"}`} data-testid="product-3d-thumb">3D</button>
            )}
          </div>
        )}
      </div>

      <div className="min-w-0">
        {cosmosDesignOn ? <CosmosBestsellerTags tags={cosmosDesign?.bestseller_tags} /> : null}
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900">{p.name}</h1>
        {p.author?.name ? (
          <p className="mt-2 text-base sm:text-lg text-stone-500">
            by{" "}
            <Link to={`${base}/author/${p.author.slug}`} className="font-semibold text-stone-800 hover:underline underline-offset-4" data-testid="product-author">{p.author.name}</Link>
          </p>
        ) : null}
        {storeOrdersPaused(config) ? (
          <div className="mt-3 rounded-lg bg-stone-900 text-white text-sm px-3 py-2" data-testid="product-store-offline">
            {storeOfflineMessage(config)}
          </div>
        ) : null}
        {p.is_digital && (
          <div className="mt-2 text-sm text-violet-800 bg-violet-50 inline-block px-2.5 py-1 rounded-full" data-testid="product-digital-badge">
            Digital product · instant access after purchase
          </div>
        )}
        {enhancer && (p.page_badges || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="product-page-badges">
            {p.page_badges.map((b, i) => (
              <span key={`${b.text}-${i}`} data-testid={`product-badge-${i}`} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold text-stone-800" style={{ background: b.color || "#FDE7F3" }}>
                <IconByName name={b.icon} className="w-3.5 h-3.5" />
                {b.text}
              </span>
            ))}
          </div>
        )}
        {enhancer ? (
          <button
            type="button"
            data-testid="product-review-summary"
            className="mt-3 flex flex-wrap items-center gap-2 text-left text-sm"
            onClick={() => {
              setTab("reviews");
              document.getElementById("product-reviews")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            <span className="font-semibold text-stone-800">Customer Reviews :</span>
            {ratingAgg.rating_count > 0 ? (
              <>
                <Stars value={Number(ratingAgg.rating_avg)} size="md" />
                <span className="font-mono-nums font-semibold text-stone-900">{Number(ratingAgg.rating_avg).toFixed(1)}</span>
                <span className="text-stone-300">|</span>
                <span className="font-mono-nums text-stone-600">{reviewCountLabel(ratingAgg.rating_count)}</span>
              </>
            ) : (
              <span className="text-stone-500">No reviews yet</span>
            )}
          </button>
        ) : ratingAgg.rating_count > 0 ? (
          <div className="mt-2 flex items-center gap-2" data-testid="product-rating-summary">
            <Stars value={Number(ratingAgg.rating_avg)} size="md" />
            <span className="text-sm text-stone-600 font-mono-nums">{Number(ratingAgg.rating_avg).toFixed(1)} · {ratingAgg.rating_count} rating{ratingAgg.rating_count === 1 ? "" : "s"}</span>
          </div>
        ) : null}
        <div className="mt-4 flex items-baseline gap-3 flex-wrap">
          <span className="font-mono-nums text-3xl font-bold text-stone-900">{money(effective.price, sym)}</span>
          {effective.mrp && effective.mrp > effective.price && (
            <>
              <span className="font-mono-nums text-stone-400 line-through">{money(effective.mrp, sym)}</span>
              <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">-{discount}%</span>
            </>
          )}
        </div>
        {enhancer ? <div className="mt-1 text-xs text-stone-500">MRP Inclusive Of All Taxes</div> : null}
        {cosmosDesignOn ? <CosmosCreativeInfo groups={cosmosDesign?.creative_info} primary={primary} /> : null}
        {enhancer && offers.length > 0 && (
          <div className="mt-5" data-testid="product-offers">
            <div className="text-sm font-semibold text-stone-900 mb-2">Applicable Offers</div>
            <div className="relative">
              <button type="button" aria-label="Previous offers" data-testid="product-offers-prev" className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-stone-200 shadow flex items-center justify-center" onClick={() => offerRow.current?.scrollBy({ left: -260, behavior: "smooth" })}><ChevronLeft className="w-4 h-4" /></button>
              <div ref={offerRow} className="flex gap-3 overflow-x-auto scroll-smooth no-scrollbar px-10">
                {offers.map((offer) => (
                  <article key={offer.code} className="snap-start shrink-0 w-[230px] rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid={`product-offer-${offer.code}`}>
                    <div className={`text-center text-[11px] font-bold tracking-wider py-1.5 ${OFFER_RIBBON[offer.tone] || OFFER_RIBBON.sky}`}>{offer.ribbon}</div>
                    <div className="p-4">
                      <div className="font-black text-lg leading-tight text-stone-900">{offer.title}</div>
                      {offer.description ? <p className="text-sm text-stone-600 mt-1">{offer.description}</p> : null}
                      {(offer.notes || []).map((line) => <p key={line} className="text-xs text-stone-500 mt-1">{line}</p>)}
                      <div className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-stone-400">Apply code</div>
                      <button
                        type="button"
                        data-testid={`product-offer-code-${offer.code}`}
                        className={`mt-1 w-full h-9 rounded-lg text-sm font-bold ${OFFER_BUTTON[offer.tone] || OFFER_BUTTON.sky}`}
                        onClick={async () => {
                          try { await navigator.clipboard.writeText(offer.code); toast.success(`${offer.code} copied. Apply it at checkout.`); }
                          catch { toast.success(offer.code); }
                        }}
                      >{offer.code}</button>
                    </div>
                  </article>
                ))}
              </div>
              <button type="button" aria-label="Next offers" data-testid="product-offers-next" className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-stone-200 shadow flex items-center justify-center" onClick={() => offerRow.current?.scrollBy({ left: 260, behavior: "smooth" })}><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        {stockHidden ? (
          <div className="mt-2 text-sm text-emerald-700 font-medium">Made to order</div>
        ) : effective.stock === null || effective.stock === undefined ? (
          <div className="mt-2 text-sm text-emerald-700 font-medium">In stock</div>
        ) : effective.stock > 0 ? (
          <div className="mt-2 text-sm text-emerald-700 font-medium">In stock · {effective.stock} available</div>
        ) : (
          <div className="mt-2 text-sm text-red-600 font-medium">Out of stock</div>
        )}

        {p.short_description && <p className="mt-5 text-stone-700 leading-relaxed whitespace-pre-wrap" data-testid="product-short-description">{p.short_description}</p>}

        {p.variants && p.variants.length > 0 && !activeVariant ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="select-variant-hint">
            Please select a variation to add to cart
          </div>
        ) : null}

        {/* Variant selectors */}
        {p.variants && p.variants.length > 0 && (
          <div className="mt-6 space-y-4">
            {sizes.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Size</div>
                <div className="flex flex-wrap gap-2">
                  {sizes.map((sz) => {
                    const v = findVariant(sz, activeVariant?.attributes?.color);
                    const selected = activeVariant?.attributes?.size === sz;
                    const disabled = !v || (!stockHidden && v.stock != null && v.stock <= 0);
                    return (
                      <button key={sz} type="button" disabled={disabled} onClick={() => v && setVariantId(v.id)} data-testid={`variant-size-${sz}`} className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${selected ? "text-white" : "text-stone-800 bg-white hover:border-stone-400"} ${disabled ? "opacity-40 line-through cursor-not-allowed" : ""}`} style={selected ? { background: primary, borderColor: primary } : { borderColor: "#e7e5e4" }}>
                        {sz}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {colors.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Color <span className="text-stone-400 font-normal normal-case tracking-normal">· {activeVariant?.attributes?.color || "Select"}</span></div>
                <div className="flex flex-wrap gap-2">
                  {colors.map((col) => {
                    const v = findVariant(activeVariant?.attributes?.size, col);
                    const selected = activeVariant?.attributes?.color === col;
                    const disabled = !v || (!stockHidden && v.stock != null && v.stock <= 0);
                    return (
                      <button key={col} type="button" disabled={disabled} onClick={() => v && setVariantId(v.id)} data-testid={`variant-color-${col}`} className={`px-4 py-2 rounded-full border text-sm font-medium flex items-center gap-2 ${selected ? "border-stone-900 bg-stone-50" : "border-stone-200 bg-white"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
                        <span className="w-3.5 h-3.5 rounded-full border border-stone-300" style={{ background: colorHex[col] || col.toLowerCase() }} />
                        {col}
                        {selected && <Check className="w-3.5 h-3.5" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Variant list fallback if no size/color axes */}
            {sizes.length === 0 && colors.length === 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Options</div>
                <div className="flex flex-wrap gap-2">
                  {p.variants.map((v) => {
                    const selected = variantId === v.id;
                    const disabled = !stockHidden && v.stock != null && v.stock <= 0;
                    return (
                      <button key={v.id} type="button" disabled={disabled} onClick={() => setVariantId(v.id)} data-testid={`variant-option-${v.id}`} className={`px-4 py-2 rounded-full border text-sm font-medium ${selected ? "text-white" : "text-stone-800 bg-white"} ${disabled ? "opacity-40 line-through cursor-not-allowed" : ""}`} style={selected ? { background: primary, borderColor: primary } : { borderColor: "#e7e5e4" }}>
                        {v.name || "Variant"}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {trustLines.length > 0 && (
          <div className="mt-6 space-y-2" data-testid="product-trust-lines">
            {trustLines.map((row, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-stone-700" data-testid={`product-trust-${i}`}>
                <span className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center shrink-0 text-stone-800">
                  <IconByName name={row.icon} className="w-4 h-4" />
                </span>
                <span>{row.text}</span>
              </div>
            ))}
          </div>
        )}

        {fitment ? (
          <div className="mt-6 rounded-2xl border border-stone-200 bg-stone-50 p-4" data-testid="product-fitment">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">This part fits</div>
            {fitment.universal ? (
              <p className="text-sm text-stone-800 font-medium" data-testid="product-fitment-universal">Universal fit — works on all vehicles</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-stone-500">
                      <th className="py-1 pr-3 font-semibold">Make</th>
                      <th className="py-1 pr-3 font-semibold">Model</th>
                      <th className="py-1 pr-3 font-semibold">Year</th>
                      <th className="py-1 font-semibold">Trim</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(fitment.applications || []).map((app, i) => (
                      <tr key={app.id || i} className="border-t border-stone-200/80" data-testid={`product-fitment-row-${i}`}>
                        <td className="py-1.5 pr-3 text-stone-800">{app.make}</td>
                        <td className="py-1.5 pr-3 text-stone-800">{app.model}</td>
                        <td className="py-1.5 pr-3 text-stone-800 font-mono-nums">{app.year_from === app.year_to ? app.year_from : `${app.year_from}–${app.year_to}`}</td>
                        <td className="py-1.5 text-stone-600">{app.trim || "All trims"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        {printOn ? (
          <div className="mt-6 space-y-2" data-testid="product-custom-print-cta">
            <button
              type="button"
              data-testid="cp-open"
              onClick={() => setPrintOpen(true)}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm font-bold text-white shadow-sm"
              style={{ background: primary }}
            >
              <Wand2 className="w-4 h-4" /> Customize Product
            </button>
            {printSnap?.image || printSnap?.text ? (
              <p className="text-sm text-emerald-700 font-medium" data-testid="cp-saved-msg">
                Customization saved!! Now add to cart and checkout, or reopen customization.
              </p>
            ) : (
              <p className="text-sm text-stone-500">Personalize this product before adding it to your cart.</p>
            )}
            <Dialog open={printOpen} onOpenChange={setPrintOpen}>
              <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="cp-popup">
                <DialogHeader>
                  <DialogTitle>Customize Product</DialogTitle>
                </DialogHeader>
                <ProductCustomizer
                  key={printOpen ? "open" : "closed"}
                  product={p}
                  slug={slug}
                  initial={printSnap}
                  hideIntro
                  onConfirm={(snap) => {
                    setPrintSnap(snap);
                    setPrintOpen(false);
                    toast.success("Customization saved");
                  }}
                />
              </DialogContent>
            </Dialog>
          </div>
        ) : null}

        {formOn ? (
          <CustomProductForm
            product={p}
            variant={activeVariant}
            slug={slug}
            primary={primary}
            onAdd={doAdd}
            disabled={storeOrdersPaused(config)}
            disabledLabel={storeOfflineMessage(config)}
          />
        ) : (
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center rounded-full border border-stone-300 overflow-hidden">
            <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 flex items-center justify-center hover:bg-stone-100" data-testid="product-qty-dec"><Minus className="w-4 h-4" /></button>
            <span className="w-10 text-center font-mono-nums font-semibold" data-testid="product-qty">{qty}</span>
            <button onClick={() => setQty((q) => effective.stock == null ? q + 1 : Math.min(effective.stock || 99, q + 1))} className="w-9 h-9 flex items-center justify-center hover:bg-stone-100" data-testid="product-qty-inc"><Plus className="w-4 h-4" /></button>
          </div>
          <Button disabled={oos || storeOrdersPaused(config)} onClick={() => doAdd()} data-testid="product-add-to-cart" className="btn-store-primary rounded-full px-5"><ShoppingCart className="w-4 h-4 mr-1" /> {skipCart ? t("store.buyNow") : t("store.addToCart")}</Button>
          {!skipCart && <Button disabled={oos || storeOrdersPaused(config)} onClick={buyNow} variant="outline" className="rounded-full border-stone-300" data-testid="product-buy-now">{t("store.buyNow")}</Button>}
          <button type="button" onClick={() => { const added = wishlist?.toggle(p); toast.success(added ? "Added to wishlist" : "Removed from wishlist"); }} data-testid="product-wishlist" aria-label="Toggle wishlist" className="w-10 h-10 rounded-full border border-stone-300 flex items-center justify-center hover:bg-stone-50">
            <Heart className={`w-5 h-5 ${wishlist?.has(p.id) ? "text-rose-600" : "text-stone-500"}`} fill={wishlist?.has(p.id) ? "#e11d48" : "none"} />
          </button>
        </div>
        )}

        {featureIcons.length > 0 && (
          <div className={`mt-6 grid gap-3 ${featureIcons.length >= 4 ? "grid-cols-4" : featureIcons.length === 3 ? "grid-cols-3" : "grid-cols-2"}`} data-testid="product-feature-icons">
            {featureIcons.map((row, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-1.5 px-1" data-testid={`product-feature-${i}`}>
                <span className="w-10 h-10 rounded-full border border-stone-200 flex items-center justify-center text-stone-800">
                  <IconByName name={row.icon} className="w-5 h-5" />
                </span>
                <span className="text-[11px] sm:text-xs text-stone-600 leading-tight">{row.text}</span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-xs text-stone-500 space-y-1">
          {effective.sku && <div>SKU: <span className="font-mono-nums">{effective.sku}</span></div>}
        </div>
      </div>
      </div>

      {cosmosDesignOn ? (
        <>
          <CosmosDescriptionTabs product={p} design={cosmosDesign || {}} primary={primary} />
          <CosmosProductFaq faqs={cosmosDesign?.faqs} primary={primary} />
        </>
      ) : null}

      {/* Description & Reviews tabs */}
      <section className="mt-12 border-t border-stone-200 pt-8" data-testid="product-tabs">
        <div className="flex items-center gap-2 border-b border-stone-200">
          {!cosmosDesignOn && (
          <button
            type="button"
            onClick={() => setTab("description")}
            data-testid="tab-description"
            className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors ${tab === "description" ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}
          >Description</button>
          )}
          <button
            type="button"
            onClick={() => setTab("reviews")}
            data-testid="tab-reviews"
            className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors ${(cosmosDesignOn || tab === "reviews") ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}
          >Reviews{ratingAgg.rating_count > 0 ? ` (${ratingAgg.rating_count})` : ""}</button>
        </div>

        {!cosmosDesignOn && tab === "description" ? (
          <div className="pt-6" data-testid="tab-panel-description">
            {p.description ? (
              looksLikeHtml(p.description) ? (
                <div className="product-desc-prose text-stone-600 leading-relaxed max-w-3xl" dangerouslySetInnerHTML={{ __html: sanitizeProductHtml(p.description) }} />
              ) : (
                <p className="text-stone-600 leading-relaxed whitespace-pre-wrap max-w-3xl">{p.description}</p>
              )
            ) : (
              <p className="text-sm text-stone-500">No description provided for this product.</p>
            )}
          </div>
        ) : (
          <div className="pt-6" id="product-reviews" data-testid="tab-panel-reviews">
            <div className="grid md:grid-cols-[240px_1fr] gap-8">
              <div>
                {ratingAgg.rating_count > 0 ? (
                  <div className="flex items-center gap-3">
                    <div className="font-display text-4xl font-bold text-stone-900">{Number(ratingAgg.rating_avg).toFixed(1)}</div>
                    <div>
                      <Stars value={Number(ratingAgg.rating_avg)} size="md" />
                      <div className="text-xs text-stone-500 mt-1 font-mono-nums">{ratingAgg.rating_count} rating{ratingAgg.rating_count === 1 ? "" : "s"}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-stone-500">No ratings yet. Be the first!</div>
                )}

                {/* Rate widget */}
                <div className="mt-6 p-4 rounded-2xl bg-stone-50 border border-stone-200/70">
                  <div className="text-sm font-semibold text-stone-900">{myRating ? "Your rating" : "Rate this product"}</div>
                  <div className="mt-2 flex items-center gap-1" onMouseLeave={() => setHoverStar(0)}>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button key={i} type="button" disabled={submitting} onMouseEnter={() => setHoverStar(i)} onClick={() => submitRating(i)} data-testid={`rate-star-${i}`} className="p-0.5">
                        <Star className="w-6 h-6" style={{ color: (hoverStar || myRating) >= i ? "#fbbf24" : "#d6d3d1" }} fill={(hoverStar || myRating) >= i ? "#fbbf24" : "none"} />
                      </button>
                    ))}
                  </div>
                  <Textarea value={myComment} onChange={(e) => setMyComment(e.target.value)} placeholder="Share a few words (optional)" rows={2} className="mt-3 bg-white" data-testid="review-comment" />
                  {!user && <div className="mt-2 text-xs text-stone-500">You&#39;ll be asked to sign in to submit.</div>}
                </div>
              </div>

              <div>
                {reviews.length === 0 ? (
                  <div className="text-sm text-stone-500">No written reviews yet.</div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((r, i) => (
                      <div key={i} className="p-4 rounded-2xl bg-white border border-stone-200/70" data-testid={`review-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-stone-900">{r.customer_name || "Customer"}</div>
                          <Stars value={Number(r.stars)} />
                        </div>
                        {r.comment && <p className="mt-2 text-sm text-stone-600 leading-relaxed">{r.comment}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      <RelatedProductSlider
        slug={slug}
        productId={p.id}
        categorySlug={category?.slug}
        categoryName={category?.name}
        categoryPending={productCategoryIds(p).length > 0 && category === undefined}
        viewAllHref={category?.slug ? `${base}/category/${category.slug}` : `${base}/shop`}
        primary={primary}
        variant={storefrontCardVariant(config)}
      />
    </div>
  );
}
